import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import {
  FeaturesConfigFormValues,
  FeaturesConfigurationFeaturesForm,
} from 'src/screens/administration/configuration-management/features-configuration/features/edit/FeaturesConfigurationFeaturesForm'
import {
  CONFIGURATION_STATUS,
  FEATURE_CONFIG_SELECTION_TYPE_TYPE,
  LEVEL,
  PROCESS,
} from 'src/shared/constants/constants'
import {
  canUpdateFeatureConfigStatus,
  fetchAllFeatureGroups,
  fetchAllFeatureTypesForFeaturesForm,
  KapFactsheetLevelRequiresKpi,
} from 'src/screens/administration/configuration-management/features-configuration/features/edit/FeaturesConfigurationFeaturesFormUtils'
import { useClient } from 'urql'
import {
  Feature_Config_Set_Input,
  Feature_Group_Config,
  Feature_Type_Config,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useLocation, useParams } from 'react-router'
import { queryFeatureConfigById, updateFeatureConfigById } from '../featuresConfigQueries'
import { useUserLocale } from 'src/user/UserContext'
import { resolveFeaturesConfigRoot } from 'src/routing/routing-utils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

const submitFeatureConfigForm = () => {
  document.getElementById('feature-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const FeaturesConfigurationFeaturesEditPage = () => {
  const { featureConfigId } = useParams()
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const locale = useUserLocale()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<FeaturesConfigFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const [featureTypes, setFeatureTypes] = useState<Feature_Type_Config[]>([])
  const [featureGroups, setFeatureGroups] = useState<Feature_Group_Config[]>([])

  const onBackHandler = useCallback(() => {
    const processTypeFromState = state?.processType

    if (processTypeFromState !== null && processTypeFromState !== undefined) {
      navigate(resolveFeaturesConfigRoot(processTypeFromState))
    } else {
      navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatures.params({}))
    }
  }, [navigate, state])

  const onSave = useCallback(() => {
    originRef.current = 'SAVE'
    submitFeatureConfigForm()
  }, [])

  const onSaveAndBack = useCallback(() => {
    originRef.current = 'SAVE_AND_BACK'
    submitFeatureConfigForm()
  }, [])

  const fetchFeatureTypes = useCallback(async () => {
    const { fetchedFeatureTypes } = await fetchAllFeatureTypesForFeaturesForm(urqlClient, locale)()
    return fetchedFeatureTypes
  }, [urqlClient, locale])

  const fetchFeatureGroups = useCallback(async () => {
    const { fetchedFeatureGroups } = await fetchAllFeatureGroups(urqlClient, locale)()
    return fetchedFeatureGroups
  }, [urqlClient, locale])

  const fetchFeatureConfigToEdit = useCallback(async () => {
    if (!featureConfigId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ feature_config: Query_Root['feature_config'] }>(queryFeatureConfigById, { featureConfigId })
      .toPromise()

    if (error || !data?.feature_config?.[0]) {
      throw new Error('Resource not found')
    }

    const fetchedFeatureConfig = data.feature_config[0]

    const fetchedKapFactsheetLevelRequiresKpi = Object.values(LEVEL).reduce((acc, level) => {
      acc[level] = fetchedFeatureConfig.has_kpi_for_levels.includes(level) ? level : null
      return acc
    }, {} as KapFactsheetLevelRequiresKpi)

    const editingFeatureConfig: FeaturesConfigFormValues = {
      enName: fetchedFeatureConfig.names['en'],
      deName: fetchedFeatureConfig.names['de'],
      frName: fetchedFeatureConfig.names['fr'],
      itName: fetchedFeatureConfig.names['it'],
      enDescription: fetchedFeatureConfig.tooltips['en'],
      deDescription: fetchedFeatureConfig.tooltips['de'],
      frDescription: fetchedFeatureConfig.tooltips['fr'],
      itDescription: fetchedFeatureConfig.tooltips['it'],
      status: fetchedFeatureConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      selectionType: fetchedFeatureConfig.selection_type as FEATURE_CONFIG_SELECTION_TYPE_TYPE,
      featureTypeId: fetchedFeatureConfig.feature_type_id,
      featureGroupId: fetchedFeatureConfig.feature_group_id ?? null,
      processAlloc: fetchedFeatureConfig.processes,
      kapFactsheetLevels: fetchedFeatureConfig.levels,
      pfKapRequiresKpi: fetchedFeatureConfig.has_kpi_for_processes.includes(PROCESS.PF_KAP) ? PROCESS.PF_KAP : null,
      pfPgvRequiresKpi: fetchedFeatureConfig.has_kpi_for_processes.includes(PROCESS.PF_PGV) ? PROCESS.PF_PGV : null,
      kapFactsheetLevelRequiresKpi: fetchedKapFactsheetLevelRequiresKpi,
    }

    return { editingFeatureConfig }
  }, [featureConfigId, urqlClient])

  const handleSubmitLocal = useCallback(
    async (values: FeaturesConfigFormValues) => {
      if (values.featureTypeId == null) {
        notificationService.operationFailed()
        return
      }

      if (
        !canUpdateFeatureConfigStatus(
          values.status,
          values.featureTypeId,
          values.featureGroupId,
          featureTypes,
          featureGroups,
        )
      ) {
        notificationService.error(getMessage('notification.edit.feature.config.status.invalid'))
        return
      }

      const featureConfigSetInput: Feature_Config_Set_Input = {
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        tooltips: {
          de: values.deDescription ?? '',
          en: values.enDescription ?? '',
          fr: values.frDescription ?? '',
          it: values.itDescription ?? '',
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
      }

      const { data, error } = await urqlClient
        .mutation<{
          update_feature_config: Mutation_Root['update_feature_config']
        }>(updateFeatureConfigById, {
          featureConfigId,
          featureConfigSetInput,
        })
        .toPromise()

      if (!error && data?.update_feature_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [featureConfigId, featureGroups, featureTypes, getMessage, notificationService, onBackHandler, urqlClient],
  )

  const initData = useCallback(async () => {
    try {
      const [featureTypes, featureGroups] = await Promise.all([fetchFeatureTypes(), fetchFeatureGroups()])
      setFeatureTypes(featureTypes)
      setFeatureGroups(featureGroups)

      const { editingFeatureConfig } = await fetchFeatureConfigToEdit()
      setInitialValues(editingFeatureConfig)
    } catch (e) {
      notificationService.operationFailed()
    }
  }, [fetchFeatureConfigToEdit, fetchFeatureGroups, fetchFeatureTypes, notificationService])

  useEffect(() => {
    initData()
  }, [initData])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeaturesForm
              mode="EDIT"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              featureTypes={featureTypes}
              featureGroups={featureGroups}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
